/**
 * Utilidades para manejo centralizado de errores
 */

/**
 * Tipos de error comunes en la aplicación
 */
export enum ErrorType {
  VALIDATION = 'VALIDATION',
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Error estructurado de la aplicación
 */
export class AppError extends Error {
  constructor(
    public type: ErrorType,
    message: string,
    public originalError?: unknown,
    public code?: string
  ) {
    super(message);
    this.name = 'AppError';
  }
}

/**
 * Determina el tipo de error basado en el mensaje o código
 */
export function classifyError(error: unknown): ErrorType {
  if (!error) {
    return ErrorType.UNKNOWN;
  }
  
  const errorMessage = error instanceof Error ? error.message : String(error);
  const lowerMessage = errorMessage.toLowerCase();
  
  // Errores de validación
  if (
    lowerMessage.includes('validation') ||
    lowerMessage.includes('invalid') ||
    lowerMessage.includes('required') ||
    lowerMessage.includes('email') ||
    lowerMessage.includes('password')
  ) {
    return ErrorType.VALIDATION;
  }
  
  // Errores de autenticación
  if (
    lowerMessage.includes('auth') ||
    lowerMessage.includes('unauthorized') ||
    lowerMessage.includes('credentials') ||
    lowerMessage.includes('email not confirmed')
  ) {
    return ErrorType.AUTHENTICATION;
  }
  
  // Errores de autorización
  if (
    lowerMessage.includes('forbidden') ||
    lowerMessage.includes('permission') ||
    lowerMessage.includes('access denied')
  ) {
    return ErrorType.AUTHORIZATION;
  }
  
  // Errores de red
  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('connection') ||
    lowerMessage.includes('timeout')
  ) {
    return ErrorType.NETWORK;
  }
  
  // No encontrado
  if (
    lowerMessage.includes('not found') ||
    lowerMessage.includes('404') ||
    lowerMessage.includes('pgrst116')
  ) {
    return ErrorType.NOT_FOUND;
  }
  
  return ErrorType.UNKNOWN;
}

/**
 * Crea un AppError desde un error desconocido
 */
export function createAppError(
  error: unknown,
  defaultMessage: string = 'Ocurrió un error inesperado'
): AppError {
  if (error instanceof AppError) {
    return error;
  }
  
  const type = classifyError(error);
  const message =
    error instanceof Error ? error.message : String(error) || defaultMessage;
  
  return new AppError(type, message, error);
}

/**
 * Obtiene un mensaje de error amigable para el usuario
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof AppError) {
    return error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'Ocurrió un error inesperado. Por favor intenta de nuevo.';
}

/**
 * Log de error estructurado (solo en desarrollo o errores críticos)
 */
export interface ErrorLogEntry {
  context?: string;
  error: AppError;
  timestamp: string;
}

type ErrorReporter = (entry: ErrorLogEntry) => void;

const errorLogBuffer: ErrorLogEntry[] = [];
const MAX_ERROR_LOGS = 100;
let errorReporter: ErrorReporter | null = null;

function persistErrorEntry(entry: ErrorLogEntry) {
  errorLogBuffer.push(entry);
  if (errorLogBuffer.length > MAX_ERROR_LOGS) {
    errorLogBuffer.shift();
  }
  if (typeof globalThis !== 'undefined') {
    (globalThis as any).__BIAVENT_ERROR_LOGS__ = [...errorLogBuffer];
  }
  if (errorReporter) {
    try {
      errorReporter(entry);
    } catch {
      // Ignorar errores de reportadores para no romper la ejecución
    }
  }
}

export function setErrorReporter(reporter: ErrorReporter | null): void {
  errorReporter = reporter;
}

export function getErrorLogs(): ErrorLogEntry[] {
  return [...errorLogBuffer];
}

export function logError(
  error: unknown,
  context?: string,
  shouldLog: boolean = import.meta.env.DEV
): void {
  if (!shouldLog) {
    return;
  }
  
  const appError = createAppError(error);
  const entry: ErrorLogEntry = {
    context,
    error: appError,
    timestamp: new Date().toISOString(),
  };
  
  persistErrorEntry(entry);
}

/**
 * Maneja errores de Supabase y los convierte a AppError
 */
export function handleSupabaseError(error: unknown): AppError {
  if (!error) {
    return new AppError(ErrorType.UNKNOWN, 'Error desconocido');
  }
  
  // Si es un error de Supabase, extraer información relevante
  if (typeof error === 'object' && 'message' in error && 'code' in error) {
    const supabaseError = error as { message: string; code?: string };
    const type = classifyError(supabaseError);
    
    return new AppError(
      type,
      supabaseError.message,
      error,
      supabaseError.code
    );
  }
  
  return createAppError(error);
}
